import { pageQuery } from '../lib/egress.js';
import { supabase }  from '../supabase.js';
import { openClassDrill } from './drills.js';
import { hasPart } from '../lib/access.js';

let _searchText = '';

const STATUS_OPTS = [
  { value: 'in_class',      label: 'In Class' },
  { value: 'practical_exp', label: 'Practical Experience' },
  { value: 'completed',     label: 'Completed' },
  { value: 'pending',       label: 'Pending' },
  { value: 'graduated',     label: 'Graduated' },
];

const statusOpts = current => STATUS_OPTS
  .map(o => `<option value="${o.value}"${o.value === current ? ' selected' : ''}>${o.label}</option>`)
  .join('');

export async function renderClasses() {
  const el = document.getElementById('view-classes');
  el.innerHTML = `
    <div class="page-header">
      <h1>Classes</h1>
      <p id="cl-subtitle">Loading classes…</p>
    </div>
    <div class="filters-row">
      <input type="text" id="cl-search" class="search-input" placeholder="Search class or facilitator…" />
    </div>
    <div class="table-card">
      <table class="data-table">
        <thead><tr>
          <th>Class</th><th>Capacity</th><th>Start</th><th>Status</th><th>Facilitator</th>
        </tr></thead>
        <tbody id="cl-tbody"><tr><td colspan="5" class="no-data">Loading…</td></tr></tbody>
      </table>
    </div>
  `;

  const [cls, facs] = await Promise.all([
    pageQuery('classes',      { order: 'class_name',  ascending: true }),
    pageQuery('facilitators', { order: 'full_name',   ascending: true }),
  ]);

  const facById = Object.fromEntries(facs.data.map(f => [f.facilitator_id, f.full_name || '']));
  const facOpts = (current) => `
    <option value="">— unassigned —</option>
    ${facs.data.map(f => `<option value="${f.facilitator_id}" ${f.facilitator_id === current ? 'selected' : ''}>${esc(f.full_name)}</option>`).join('')}
  `;

  const subtitle = document.getElementById('cl-subtitle');
  const tbody    = document.getElementById('cl-tbody');
  const search   = document.getElementById('cl-search');

  function render(filterText) {
    const q = (filterText || '').trim().toLowerCase();
    const rows = q
      ? cls.data.filter(c => {
          const facName = facById[c.facilitator_id] || '';
          return (c.class_name || '').toLowerCase().includes(q)
              || facName.toLowerCase().includes(q);
        })
      : cls.data;

    subtitle.textContent = q
      ? `${rows.length} of ${cls.data.length} classes match "${filterText.trim()}".`
      : `${cls.data.length} classes. Assign a facilitator inline to scope their access.`;

    if (!rows.length) {
      tbody.innerHTML = `<tr><td colspan="5" class="no-data">No classes match.</td></tr>`;
      return;
    }

    const editable = hasPart('setup');   // Classes & set-up (lib/access.js)
    tbody.innerHTML = rows.map(c => `
      <tr class="clickable-row" data-class-id="${c.class_id}" data-class-name="${esc(c.class_name)}">
        <td><strong>${esc(c.class_name)}</strong></td>
        <td>${c.capacity ?? '—'}</td>
        <td>${c.start_date ?? '—'}</td>
        <td>
          <select data-class-id="${c.class_id}" class="select-input cl-status" ${editable ? '' : 'disabled'}>
            ${statusOpts(c.status)}
          </select>
        </td>
        <td>
          <select data-class-id="${c.class_id}" class="select-input cl-fac" ${editable ? '' : 'disabled'}>
            ${facOpts(c.facilitator_id)}
          </select>
        </td>
      </tr>
    `).join('');

    tbody.querySelectorAll('.cl-status').forEach(sel => {
      sel.addEventListener('click', e => e.stopPropagation());
      sel.addEventListener('change', async (e) => {
        e.stopPropagation();
        const classId   = sel.dataset.classId;
        const newStatus = sel.value;
        sel.disabled = true;
        const { error } = await supabase.from('classes').update({ status: newStatus }).eq('class_id', classId);
        sel.disabled = false;
        if (error) {
          alert(`Save failed: ${error.message}`);
          const row = cls.data.find(x => x.class_id === classId);
          sel.value = row?.status ?? '';
          return;
        }
        const row = cls.data.find(x => x.class_id === classId);
        if (row) row.status = newStatus;
        sel.style.outline = '2px solid var(--green)';
        setTimeout(() => sel.style.outline = '', 600);
      });
    });

    tbody.querySelectorAll('.cl-fac').forEach(sel => {
      sel.addEventListener('click', e => e.stopPropagation());
      sel.addEventListener('change', async (e) => {
        e.stopPropagation();
        const classId       = sel.dataset.classId;
        const facilitatorId = sel.value || null;
        sel.disabled = true;
        const { error } = await supabase.from('classes').update({ facilitator_id: facilitatorId }).eq('class_id', classId);
        sel.disabled = false;
        if (error) {
          alert(`Save failed: ${error.message}`);
          return;
        }
        const row = cls.data.find(x => x.class_id === classId);
        if (row) row.facilitator_id = facilitatorId;
        sel.style.outline = '2px solid var(--green)';
        setTimeout(() => sel.style.outline = '', 600);
      });
    });

    tbody.querySelectorAll('tr.clickable-row').forEach(row => {
      row.addEventListener('click', () => {
        openClassDrill({ classId: row.dataset.classId, className: row.dataset.className });
      });
    });
  }

  search.value = _searchText;
  search.addEventListener('input', debounce(e => { _searchText = e.target.value; render(e.target.value); }, 150));
  render(_searchText);
}

function debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }
function esc(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])); }
